import * as THREE from 'three';
import { detectQuality, type Quality } from './quality';
import { BloomEffect, EffectComposer, EffectPass, RenderPass, ToneMappingEffect, ToneMappingMode, VignetteEffect } from 'postprocessing';

/** Portrait framing keeps the open courtyard and right-hand gate in view. */
const DESIGN_ASPECT = 810 / 1440;
const DESIGN_VFOV = 43;
const PORTRAIT_VFOV = 58;
/** Camera framing per mode, as [landscape, portrait] pairs. */
const COOP = { pitch: [47, 56], dist: [18.6, 19], tz: [0.75, 2.2], cx: [-0.6, 0.4] };
const SOLO = { pitch: [51, 58], dist: [14.2, 17.6], tz: [2.05, 3.1], cx: [-0.5, 0.1] };

/** MSAA only pays off at low pixel ratios; on dense displays the pixels do the AA. */
const msaaFor = (q: Quality, dpr: number) => (q.msaa && dpr < 1.4 ? 4 : 0);

/**
 * Owns the WebGL renderer, the post chain and the camera framing.
 * Tone mapping happens once, in the post pass, so emissive values above 1
 * survive into the bloom as real HDR energy.
 */
export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(DESIGN_VFOV, DESIGN_ASPECT, 0.1, 100);
  readonly composer: EffectComposer;
  readonly bloom: BloomEffect;
  /** Base camera pose; shake offsets are applied on top every frame. */
  readonly camBase = new THREE.Vector3(-0.25, 15.4, 13.4);
  readonly camTarget = new THREE.Vector3(-0.25, 0, 0.5);

  private dpr: number;
  readonly quality: Quality;
  /** The tier this device gets without a choice (settings: «Авто»). */
  readonly autoTier: Quality['tier'];
  private readonly host: HTMLElement;

  /** Solo frames a single altar: the empty partner side is dropped and the camera comes closer. */
  readonly solo: boolean;

  constructor(host: HTMLElement, override?: string | null, solo = false) {
    this.host = host;
    this.solo = solo;
    this.renderer = new THREE.WebGLRenderer({ antialias: false, stencil: false, depth: true, powerPreference: 'high-performance' });
    const quality = (this.quality = detectQuality(this.renderer.getContext(), override));
    this.autoTier = override ? detectQuality(this.renderer.getContext()).tier : quality.tier;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // Shadows are re-rendered only when static content changes (see invalidateShadows).
    this.renderer.shadowMap.autoUpdate = false;
    host.appendChild(this.renderer.domElement);

    this.dpr = Math.min(devicePixelRatio, quality.maxDpr, this.pixelCap());
    this.renderer.setPixelRatio(this.dpr);

    this.composer = new EffectComposer(this.renderer, { frameBufferType: THREE.HalfFloatType, multisampling: msaaFor(quality, this.dpr) });
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new BloomEffect({ mipmapBlur: true, luminanceThreshold: 0.82, luminanceSmoothing: 0.25, intensity: 1.35, radius: 0.72, resolutionScale: quality.bloomScale });
    this.bloom.mipmapBlurPass.levels = quality.bloomLevels;
    const tone = new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC });
    const vignette = new VignetteEffect({ offset: 0.32, darkness: 0.62 });
    this.composer.addPass(new EffectPass(this.camera, this.bloom, tone, vignette));

    // the distance fades into a cold moonlit haze rather than dead black
    this.scene.background = new THREE.Color(0x07090e);
    this.scene.fog = new THREE.FogExp2(0x0a0d14, 0.021);
    // frame first, then place the camera, so even the very first frame uses this mode's framing
    this.resize();
    this.camera.position.copy(this.camBase);
    this.camera.lookAt(this.camTarget);
    new ResizeObserver(() => this.resize()).observe(host);
  }

  /** 0 in landscape … 1 on a phone held upright (drives portrait-only framing and scene tweaks). */
  portrait = 0;

  get shadowSize() { return this.quality.shadowSize; }

  invalidateShadows() { this.renderer.shadowMap.needsUpdate = true; }

  /**
   * When the GPU time is measurable, the pixel budget is only where the resolution starts: the
   * adaptive controller may climb past it up to the display's own density if the GPU has room.
   */
  beyondBudget = false;
  /** Highest DPR the controller may reach. */
  get maxDpr() { return Math.min(this.quality.maxDpr, devicePixelRatio, this.beyondBudget ? Infinity : this.pixelCap()); }
  get minDpr() { return Math.min(this.quality.minDpr, devicePixelRatio, this.pixelCap()); }

  /** MSAA is decided once at boot: switching it on while lowering the resolution made frames both softer and costlier. */
  setDpr(dpr: number) {
    dpr = Math.max(this.minDpr, Math.min(dpr, this.maxDpr));
    if (Math.abs(dpr - this.dpr) < 0.01) return;
    this.dpr = dpr;
    this.renderer.setPixelRatio(dpr);
    this.resize();
  }
  getDpr() { return this.dpr; }
  /** Highest DPR that keeps the drawing buffer within the tier's pixel budget. */
  private pixelCap() {
    const w = this.host.clientWidth || innerWidth, h = this.host.clientHeight || innerHeight;
    return Math.max(0.5, Math.sqrt(this.quality.maxPixels / Math.max(1, w * h)));
  }

  resize() {
    const w = this.host.clientWidth || innerWidth, h = this.host.clientHeight || innerHeight;
    const cap = this.pixelCap();
    if (!this.beyondBudget && this.dpr > cap + 0.01) { this.dpr = cap; this.renderer.setPixelRatio(cap); }
    const aspect = w / h;
    // Phones in portrait: look down more steeply so the field's depth unrolls over the tall screen,
    // centre on the altars and trim the side margins a little. Landscape keeps the design pose.
    const p = THREE.MathUtils.smoothstep(0.75 - aspect, 0, 0.29);
    this.portrait = p;
    // a tall screen has height to spare once the width is used up: a steeper look unrolls the field's depth
    // over it, and the frame centres the field between the top edge and the controls
    const F = this.solo ? SOLO : COOP;
    const pitch = THREE.MathUtils.degToRad(THREE.MathUtils.lerp(F.pitch[0], F.pitch[1], p)), dist = THREE.MathUtils.lerp(F.dist[0], F.dist[1], p);
    const tz = THREE.MathUtils.lerp(F.tz[0], F.tz[1], p);
    // Portrait is width-bound: balance the left-hand totem against the gate, with both inside the frame.
    const cx = THREE.MathUtils.lerp(F.cx[0], F.cx[1], p);
    this.camTarget.set(cx, 0, tz);
    this.camBase.set(cx, Math.sin(pitch) * dist, tz + Math.cos(pitch) * dist);
    // Narrow screens need room for both the spawn fog and the diagonal gate.
    const tanHalfH = Math.tan(THREE.MathUtils.degToRad(PORTRAIT_VFOV / 2)) * DESIGN_ASPECT * (1 + 0.02 * p);
    const vfov = THREE.MathUtils.radToDeg(2 * Math.atan(Math.max(tanHalfH / aspect, Math.tan(THREE.MathUtils.degToRad(DESIGN_VFOV / 2)))));
    this.camera.fov = vfov;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
    this.composer.setSize(w, h, false);
    // the bloom's bright-pass runs at the bloom's own (half) resolution, not full screen: its output only
    // feeds the half-resolution blur chain anyway (profiled: about a tenth of the frame saved)
    this.bloom.luminancePass.setSize(this.bloom.resolution.width, this.bloom.resolution.height);
  }

  render(dt: number) { this.composer.render(dt); }
}
